import { useState, useEffect, useMemo, useRef } from "react";
import classnames from "classnames";
import { useTable, useSortBy, SortingRule, Column } from "react-table";
import { ChampionLink } from "@ugg/shared/components/TierList/common/ChampionLink";
import { TableScrollHandler, useLazyLoadTableRows } from "@ugg/shared/components/TierList/common/TableScrollHandler";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { getTierColor } from "@ugg/shared/utils/tier-helpers";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { getRoleImg } from "@ugg/shared/utils/role-helpers";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { ObjectivesChampionInfo } from "@ugg/shared/api/data-parser/tier-lists/objectives-tier-list";

interface ObjectivesTierListTableProps {
  data: ObjectivesChampionInfo[];
  filters: {
    queueType?: Partial<QueueTypeS>;
    allChampions?: "true" | "false";
  };
}

export const ObjectivesTierListTable = (props: ObjectivesTierListTableProps) => {
  const { getChampionName } = getRiotAssetsContext();
  const prevSort = useRef<SortingRule<ObjectivesChampionInfo>[] | null>(null);
  const maxPageSize = 50;
  const [pageSize, setPageSize] = useState(maxPageSize);
  const { data, filters } = props;

  const tableData = useMemo(() => {
    if (!data) return [];

    let filteredData = data;
    if (filters.allChampions !== "true") {
      filteredData = filteredData.filter((champion) => {
        return champion.pick_rate > 1;
      });
    }

    return filteredData;
  }, [data, filters.allChampions]);

  const isAram = filters.queueType === QueueTypeS.ARAM;
  const showRole = !isAram;
  const showVision = !isAram;
  const showJungleCS = !isAram;

  const hiddenColumns = [!showRole && "role", !showJungleCS && "jungle_cs", !showVision && "wards_killed"]
    .filter((col) => !!col)
    .map((col) => String(col));

  const columns = useMemo<Column<ObjectivesChampionInfo>[]>(
    () => [
      {
        id: "rank",
        accessor: "role",
        Header: "Rank",
        responsiveClassName: "xxxs:min-w-[50px] xxxs:max-w-[50px]",
        headerClassName: "rank xxxs:max-xs:invisible",
        className: "rank",
        Cell: ({ index }) => <span>{index + 1}</span>,
        sortable: false,
      },
      {
        Header: "Role",
        responsiveClassName: "xxxs:min-w-[40px]  xxxs:max-w-[60px]",
        headerClassName: "role",
        className: "role",
        accessor: "role",
        Cell: ({ value }) => (
          <div>
            <img className="w-[20px] h-[20px]" src={getRoleImg(value)} alt={value} />
          </div>
        ),
      },
      {
        Header: "Champion",
        responsiveClassName: "min-w-[135px] max-w-[180px]",
        className: "champion",
        headerClassName: "champion",
        accessor: "champion_link",
        sortType: (a, b) => {
          const champNameA = getChampionName(a.values.champion_link.champion_id);
          const champNameB = getChampionName(b.values.champion_link.champion_id);
          return champNameB.localeCompare(champNameA);
        },
        Cell: ({ value }) => <ChampionLink dim={30} championId={Number(value.champion_id)} role={value.role} />,
      },
      {
        id: "winrate",
        accessor: "win_rate",
        Header: "Win Rate",
        responsiveClassName: "min-w-[70px]",
        headerClassName: "winrate",
        className: "winrate",
        Cell: ({ value }) => (
          <span className={getTierColor(Number(value.toFixed(2)))}>
            <b>{value.toFixed(2) + "%"}</b>
          </span>
        ),
      },
      {
        Header: "Gold",
        accessor: "gold",
        responsiveClassName: "min-w-[65px]",
        Cell: ({ value }) => <span>{numberWithCommas(value)}</span>,
      },
      {
        Header: "CS",
        accessor: "cs",
        responsiveClassName: "min-w-[65px]",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Jungle CS",
        accessor: "jungle_cs",
        responsiveClassName: "min-w-[70px]",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Turrets",
        accessor: "turrets",
        responsiveClassName: "min-w-[65px]",
        Cell: ({ value }) => <span>{numberWithCommas(value)}</span>,
      },
      {
        Header: () => (
          <span>
            Damage
            <br />
            to Turrets
          </span>
        ),
        accessor: "damage_to_turrets",
        responsiveClassName: "min-w-[65px]",
        Cell: ({ value }) => <span>{numberWithCommas(value)}</span>,
      },
      {
        Header: () => (
          <span>
            Wards
            <br />
            Killed
          </span>
        ),
        accessor: "wards_killed",
        responsiveClassName: "min-w-[65px]",
        Cell: ({ value }) => <span>{numberWithCommas(value)}</span>,
      },
      {
        Header: () => (
          <span>
            Match
            <br />
            Time
          </span>
        ),
        accessor: "match_duration",
        responsiveClassName: "min-w-[65px]",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Matches",
        accessor: "matches",
        responsiveClassName: "min-w-[65px]",
        Cell: ({ value }) => <span>{numberWithCommas(value)}</span>,
      },
    ],
    [],
  );

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, preSortedRows, prepareRow, setHiddenColumns } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: prevSort.current || [{ id: "winrate", desc: true }],
        hiddenColumns,
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  useEffect(() => {
    setHiddenColumns(hiddenColumns);
  }, [filters]);

  useEffect(() => {
    setPageSize(maxPageSize);
  }, [tableData]);

  const tableRef = useRef<HTMLDivElement>(null);
  useLazyLoadTableRows({
    tableData,
    tableRef,
    pageSize,
    setPageSize,
  });

  useEffect(() => {
    if (prevSort.current) {
      if (prevSort.current[0].id !== state.sortBy[0].id || prevSort.current[0].desc !== state.sortBy[0].desc) {
        prevSort.current = state.sortBy;
        setPageSize(maxPageSize);
      }
    } else {
      prevSort.current = state.sortBy;
    }
  }, [state.sortBy]);

  return (
    <div>
      <div className="w-full overflow-x-auto rounded-sm bg-purple-400">
        <div
          ref={tableRef}
          {...getTableProps({ className: "content-section ReactTable ugg-table-2 objectives-table min-w-[900px]" })}
        >
          <div className={`rt-thead -header`}>
            {headerGroups.map((headerGroup, index) => (
              <div {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
                {headerGroup.headers.map((column, index) => {
                  const thClassNames = classnames([
                    "rt-th",
                    column.headerClassName,
                    column.responsiveClassName,
                    column.isSorted && !column.isSortedDesc ? "-sort-asc" : "",
                    column.isSorted && column.isSortedDesc ? "-sort-desc" : "",
                  ]);

                  return (
                    <div className={thClassNames} {...column.getHeaderProps(column.getSortByToggleProps())}>
                      {column.render("Header")}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div {...getTableBodyProps({ className: "rt-tbody" })}>
            {rows.map((row, i) => {
              if (i >= pageSize) {
                return null;
              }
              prepareRow(row);
              return (
                <div key={i} className="rt-tr-group">
                  <div {...row.getRowProps({ className: "rt-tr" })}>
                    {row.cells.map((cell, index) => {
                      const tdClassNames = classnames("rt-td", cell.column.responsiveClassName, cell.column.className, {
                        sorted: cell.column.isSorted,
                        "is-in-odd-row": i % 2 === 0,
                      });

                      return <div {...cell.getCellProps({ className: tdClassNames })}>{cell.render("Cell", { index: i })}</div>;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <TableScrollHandler />
    </div>
  );
};
